import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, IconButton, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PromptsTab from './PromptsTab';
import ChipDropper from "./dragable/ChipDropper";
import {deletePrompt, getPrompt, updatePrompt} from "../services/PromptsService";
import {useMessageContext} from "../contexts/MessageContext";

const PromptEditor = ({ company }) => {
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const {notificationHandler} = useMessageContext();
  const chipDropperRef = useRef(null);

  // Handle when a prompt is selected from the list
  const handlePromptSelect = async (prompt) => {
    try {
      setLoading(true);

      // Use the prompt ID passed from the clicked item
      const response = await getPrompt(prompt.id);

      // Update state with the complete prompt data
      setSelectedPrompt(response.data);
      setIsEditing(true);
    } catch (err) {
      console.error('Error loading prompt:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setLoading(true);
    deletePrompt(selectedPrompt.prompt.id)
        .then(() => notificationHandler.success('Prompt deleted'))
        .then(handleBack)
        .catch(() => notificationHandler.error('Can Not Delete a prompt that is in use'))
        .finally(() => setLoading(false));
  }

  // Handle going back to the prompt list
  const handleBack = () => {
    setIsEditing(false);
    setSelectedPrompt(null);
  };

  // Handle saving changes with both content and description
  const handleSave = (content) => {
    setLoading(true);

    // Create updated prompt object with both content and description
    const updatedPrompt = {
      id: selectedPrompt.prompt.id,
      content: content,
    };

    // Use the updatePrompt service function
    updatePrompt(updatedPrompt)
        .then(() => notificationHandler.success('Prompt updated successfully'))
        .then(handleBack)
        .catch(() => notificationHandler.error('Failed to save changes'))
        .finally(() => setLoading(false));
  };

  return (
      <Box sx={{ width: '100%', p: 2 }}>
        {isEditing ? (
            // Editor view
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5">
                  {selectedPrompt?.prompt.name || 'Loading...'}
                </Typography>
              </Box>

              {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                  </Box>
              ) : (
                  <>
                    {/* The ChipDropper component for editing the prompt */}
                    <ChipDropper
                        subCategory={selectedPrompt?.prompt.subCategory}
                        initialText={selectedPrompt?.content || ''}
                        initialDescription={selectedPrompt?.prompt.description || ''}
                        promptId={selectedPrompt?.prompt.id}
                        onSave={handleSave}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                          color={"error"}
                          variant="text"
                          onClick={handleDelete}
                          sx={{ mr: 1 }}
                      >
                        Delete
                      </Button>
                      <Button
                          variant="outlined"
                          onClick={handleBack}
                          sx={{ mr: 1 }}
                      >
                        Cancel
                      </Button>
                      <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            const content = document.querySelector('textarea').value;
                            handleSave(content);
                          }}
                      >
                        Save Changes
                      </Button>
                    </Box>
                  </>
              )}
            </Box>
        ) : (
            // List view
            <PromptsTab
                company={company}
                onPromptSelect={handlePromptSelect}
            />
        )}
      </Box>
  );
};

export default PromptEditor;
